import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AppShell } from '../components/layout/AppShell'
import { PermissionGate } from '../components/auth/PermissionGate'
import { DashboardScaffold } from '../components/dashboard/DashboardScaffold'
import { Button } from '../components/ui/button'
import { DASHBOARD_ACTIONS, DASHBOARD_WIDGETS } from '../config/navigation'
import { useAuth } from '../context/AuthContext'
import { dashboardService } from '../api/services/dashboard.service'
import type { DashboardStats } from '../types/dashboard'

export function DashboardPage() {
  const { session, can, roleLabel } = useAuth()
  const firstName = session?.user.first_name ?? 'there'
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [statsError, setStatsError] = useState(false)

  useEffect(() => {
    let cancelled = false

    void (async () => {
      try {
        const response = await dashboardService.getStats()
        if (!cancelled) {
          setStats(response.data)
          setStatsError(false)
        }
      } catch {
        if (!cancelled) {
          setStats(null)
          setStatsError(true)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [])

  const visibleWidgets = DASHBOARD_WIDGETS.filter((widget) => {
    if (widget.permission && !can(widget.permission)) {
      return false
    }

    if (!widget.statKey) {
      return true
    }

    // Role-scoped API: only show tiles present in the payload for this role.
    if (stats === null) {
      return !statsError
    }

    return Object.prototype.hasOwnProperty.call(stats, widget.statKey)
  }).map((widget) => {
    if (widget.statKey && stats && Object.prototype.hasOwnProperty.call(stats, widget.statKey)) {
      const raw = stats[widget.statKey]
      return {
        ...widget,
        value: raw == null ? '—' : String(raw),
      }
    }

    return widget
  })

  const visibleActions = DASHBOARD_ACTIONS.filter((action) => can(action.permission))

  return (
    <AppShell>
      <DashboardScaffold
        title={`Good afternoon, ${firstName}`}
        description={`${roleLabel} dashboard — modules and actions below reflect your assigned permissions.`}
        stats={visibleWidgets}
      >
        {statsError && (
          <p className="mb-4 text-sm text-red-700">
            Dashboard stats could not be loaded. Refresh the page to try again.
          </p>
        )}

        {visibleActions.length > 0 && (
          <div className="mb-6 flex flex-wrap gap-2">
            {visibleActions.map((action) =>
              action.to ? (
                <Button key={action.permission} asChild>
                  <Link to={action.to}>{action.label}</Link>
                </Button>
              ) : (
                <Button key={action.permission} disabled>
                  {action.label}
                </Button>
              ),
            )}
          </div>
        )}

        <section className="rounded-lg border border-parch-100 bg-white p-6 shadow-card">
          <h2 className="font-display text-xl font-bold">Dashboard modules</h2>
          <p className="mt-2 text-sm text-ink-600">
            The shell, sidebar, and dashboard layout are shared for every role. Individual
            modules, KPIs, and actions appear only when your role has been granted the
            matching permission by a super-admin.
          </p>

          <PermissionGate permission="doctors-list">
            <p className="mt-4 text-sm text-green-700">
              Review doctor accounts in{' '}
              <Link className="font-semibold underline" to="/admin/doctor-approvals">
                Doctors
              </Link>
              .
            </p>
          </PermissionGate>

          <PermissionGate permission="cases-create">
            <p className="mt-4 text-sm text-green-700">
              Ready to document a case?{' '}
              <Link className="font-semibold underline" to="/cases/new">
                Upload a New Case
              </Link>
              .
            </p>
          </PermissionGate>

          <PermissionGate permission="cases-list">
            <p className="mt-2 text-sm text-green-700">
              Browse{' '}
              <Link className="font-semibold underline" to="/cases">
                My Cases
              </Link>{' '}
              (permission: cases-list).
            </p>
          </PermissionGate>

          {!visibleWidgets.length && !visibleActions.length && (
            <p className="mt-4 text-sm text-ink-400">
              No dashboard widgets are visible yet. A super-admin can assign permissions to
              your role.
            </p>
          )}
        </section>
      </DashboardScaffold>
    </AppShell>
  )
}
